import { humanize, pluralize } from 'inflected';
import { ColumnConfig } from '@blisspointmedia/bpm-types/dist/ColumnConfig';
import { ColumnOrMetricName, getConfig } from '@blisspointmedia/bpm-types/dist/Columns';
import { getChannelPrettyName } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import {
  ChartFilter,
  ColumnCurrency,
  DateGrouping,
  FilterNode,
  FilterOp,
  GrainRef,
  hasMetricFilters,
  isAllGroup,
  isAnyGroup,
  isFilterLeaf,
  isFilterValue,
  isKpiQuery,
  isKpiVolumesQuery,
  isScpQuery,
  isWellFormedGrain,
  METRIC_FILTER_OPS,
  MetricFilter,
  MetricFilterOp,
  QueryResponse,
  QuerySpec,
  ScpQuerySpec
} from '@blisspointmedia/bpm-types/dist/querying';
import {
  ChartConfig,
  FieldMetaCtx,
  getFieldMeta
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { DimensionId } from '@blisspointmedia/bpm-types/dist/querying/ids';
import { activeBreakdownFor, withActiveBreakdown } from './chart-renderer-utils';

// Readable words over symbols — the context line is read by non-technical users too.
const FILTER_OP_LABELS: Record<FilterOp, string> = {
  in: 'in',
  notIn: 'not in',
  eq: 'is',
  ne: 'is not',
  contains: 'contains',
  notContains: 'does not contain'
};

const formatFilterValues = ({ op, values }: ChartFilter): string => {
  if (op === 'contains' || op === 'notContains') return values.map(v => `"${v}"`).join(', ');
  return values.join(', ');
};

// Dimension label = the registry pretty name ("Campaign Name") when known, else the bare id
// (already human for custom segments).
export const filterDimensionLabel = (id: DimensionId): string => {
  const cfg = getConfig(id as ColumnOrMetricName) as ColumnConfig | undefined;
  return cfg?.prettyName ?? id;
};

// e.g. `Campaign in Nike, Adidas` / `Campaign contains "awareness"`.
const formatFilter = (filter: ChartFilter): string => {
  const label = filterDimensionLabel(filter.dimension.id);
  return `${label} ${FILTER_OP_LABELS[filter.op]} ${formatFilterValues(filter)}`;
};

// A saved config is untrusted JSON, so a leaf must have the fields formatFilter reads (an unknown
// `op` would render "undefined", and no values would render "Platform in ").
const isRenderableLeaf = (node: FilterNode): node is ChartFilter =>
  isFilterLeaf(node) &&
  typeof node.dimension?.id === 'string' &&
  Array.isArray(node.values) &&
  node.values.length > 0 &&
  node.values.every(isFilterValue) &&
  Object.hasOwn(FILTER_OP_LABELS, node.op);

// A leaf renders as formatFilter; a group as its children joined by " or " (any) / " and " (all) in
// parentheses. Malformed nodes and groups whose children all render to nothing render nothing.
export const formatFilterNode = (node: FilterNode): string => {
  if (isRenderableLeaf(node)) return formatFilter(node);
  const isAny = isAnyGroup(node);
  if (!isAny && !isAllGroup(node)) return '';
  const children = isAny ? node.any : node.all;
  if (!Array.isArray(children)) return '';
  const parts = children.map(formatFilterNode).filter(Boolean);
  return parts.length > 0 ? `(${parts.join(isAny ? ' or ' : ' and ')})` : '';
};

// One query's source display: the analytics source-of-truth value(s) (e.g. "ga4") when scoped,
// else the bare source id ("platform", "google_ads_data_hub"). Names the SCP member directly:
// indexed access on the union yields only keys present in every member.
const sourceDisplay = (source: ScpQuerySpec['source']): string =>
  source.values && source.values.length > 0 ? source.values.join(', ') : source.id;

const uniq = (values: string[]): string[] => [...new Set(values)];

// Descriptor segment for a KPI Volumes query (not "Label: value", like a filter segment).
export const RAW_KPI_COUNTS_LABEL = 'Raw KPI counts (unattributed)';

// A saved config is untrusted JSON: a metricFilters entry can be `null`, not an object, or missing/
// bad `metric.id`, `values`, `op`, or `grain`. Guard each entry individually — rather than just the
// array — since one bad entry must not blank the whole card title (there's no error boundary below
// Page). An unknown `op` is rejected here (not left to METRIC_FILTER_OP_LABELS' index) so a bad entry
// can't render the literal word "undefined" into the title.
const isValidMetricFilter = (filter: unknown): filter is MetricFilter =>
  typeof filter === 'object' &&
  filter !== null &&
  typeof (filter as MetricFilter).metric?.id === 'string' &&
  Array.isArray((filter as MetricFilter).values) &&
  METRIC_FILTER_OPS.includes((filter as MetricFilter).op) &&
  // The validator's own definition of a well-formed grain, so the caption and the validator agree.
  ((filter as MetricFilter).grain === undefined ||
    isWellFormedGrain((filter as MetricFilter).grain));

// Readable words over symbols, matching FILTER_OP_LABELS' register.
const METRIC_FILTER_OP_LABELS: Record<MetricFilterOp, string> = {
  gt: 'over',
  gte: 'at least',
  lt: 'under',
  lte: 'at most',
  eq: 'is',
  ne: 'is not',
  between: 'between'
};

// A `*_native` metric is money in each account's own currency, so a threshold on it is compared
// against every row in that row's currency. Registry-flagged base columns (`spend_native`) plus the
// naming convention for derived native metrics (`cpcNative`), which the registry doesn't flag.
const isNativeCurrencyMetric = (metricId: string): boolean =>
  Boolean(
    (getConfig(metricId as ColumnOrMetricName) as ColumnConfig | undefined)?.isNativeCurrency
  ) || /(_native|Native)$/.test(metricId);

// The noun a date bucket reads as ("Days with…", "Campaigns per week with…").
const DATE_GROUPING_NOUNS: Record<DateGrouping, string> = {
  daily: 'Day',
  weekly: 'Week',
  monthly: 'Month',
  quarterly: 'Quarter',
  yearly: 'Year'
};

// One grain dimension as a singular noun: "Campaign Name" → "Campaign", `date` → its bucket. A
// dimension with no registry label is humanized from its id ("ad_group" → "Ad group").
const grainNoun = (g: GrainRef): string => {
  if (g.id === 'date') return DATE_GROUPING_NOUNS[g.grouping ?? 'daily'];
  const cfg = getConfig(g.id as ColumnOrMetricName) as ColumnConfig | undefined;
  return (cfg?.prettyName ?? humanize(g.id)).replace(/ name$/i, '');
};

// "Campaigns with " — what a metric filter is tested against: each group of its grain (the filter's
// own `grain` when set, else the query's dimensions; see MetricFilter). The lead noun is the first
// non-date dimension, pluralized; any others qualify it ("Campaigns by platform per day with ").
// Empty for a query with no dimensions (one total row), where there's nothing to name.
const grainPrefix = (grain: MetricFilter['grain'], dimensions: QuerySpec['dimensions']): string => {
  const groups: GrainRef[] = Array.isArray(grain) && grain.length > 0 ? grain : (dimensions ?? []);
  const [lead, ...rest] = [
    ...groups.filter(g => g.id !== 'date'),
    ...groups.filter(g => g.id === 'date')
  ];
  if (!lead) return '';
  const qualifiers = rest.map(g => {
    const noun = grainNoun(g).toLowerCase();
    return g.id === 'date' ? ` per ${noun}` : ` by ${noun}`;
  });
  return `${pluralize(grainNoun(lead))}${qualifiers.join('')} with `;
};

// e.g. `Campaigns with Spend over $100,000` / `Days with CTR at least 2%`. The threshold is stored
// in the metric's raw unit (a fraction for percents) and formatted with the currency the response
// reported for that column, as the table does. A native-currency metric with no single reported
// currency gets a plain number instead, since no one symbol is right for every row.
const formatMetricFilter = (
  queryId: string,
  { metric, op, values, grain }: MetricFilter,
  dimensions: QuerySpec['dimensions'],
  fieldMetaCtx?: FieldMetaCtx,
  currency?: ColumnCurrency
): string => {
  const ref = `${queryId}.${metric.id}`;
  const noSingleCurrency =
    currency?.kind === 'mixed' || (currency === undefined && isNativeCurrencyMetric(metric.id));
  const meta = getFieldMeta(ref, {
    ctx: fieldMetaCtx,
    role: 'metric',
    currency,
    format: noSingleCurrency ? { kind: 'number' } : undefined
  });
  const shown =
    op === 'between' && values.length === 2
      ? `${meta.format(values[0])} and ${meta.format(values[1])}`
      : meta.format(values[0]);
  const suffix = noSingleCurrency ? ' (native currency)' : '';
  return `${grainPrefix(grain, dimensions)}${meta.label} ${METRIC_FILTER_OP_LABELS[op]} ${shown}${suffix}`;
};

// The per-column currency the response reported, keyed by output ref (e.g. "q0.spend_native").
export const columnCurrencyByRef = (data: QueryResponse): Record<string, ColumnCurrency> =>
  Object.fromEntries(
    Object.values(data.queries).flatMap(block =>
      (block?.schema ?? []).flatMap(c => (c.currency ? [[c.id, c.currency]] : []))
    )
  );

export interface ChartContextOptions {
  /**
   * Include the KPI in the line. Off by default: the focused view already shows a KPI picker, so
   * repeating it is noise — only pass this where no KPI picker is visible.
   */
  includeKpi?: boolean;
  /**
   * Company currency + custom-metric display metadata, so a metric filter's threshold renders the
   * way its column does. Optional: without it, registry formatting and the default currency apply.
   */
  fieldMetaCtx?: FieldMetaCtx;
  /**
   * Per-column currency from the response (see `columnCurrencyByRef`), so a native-currency
   * threshold reads in the currency its rows are actually in rather than the company default.
   */
  currencyByRef?: Record<string, ColumnCurrency>;
}

/**
 * Where a chart's numbers come from: channel(s) → raw-count marker → source(s) → KPI(s). Channel and
 * source are ALWAYS shown (no inference about "is this worth surfacing?" — that produced the confusing
 * case where one chart named its source and a near-identical one didn't); each lists the distinct
 * values across a multi-query chart. A KPI Volumes (raw KPI count) query has no channel, so it adds a
 * marker saying the numbers are raw and unattributed (they won't match the attributed KPI results
 * shown elsewhere), and names its `sources` — which measurement it is.
 */
export const buildChartSourceSegments = (
  queries: QuerySpec[],
  { includeKpi = false }: ChartContextOptions = {}
): string[] => {
  const segments: string[] = [];
  const scpQueries = queries.filter(isScpQuery);
  const rawKpiQueries = queries.filter(isKpiVolumesQuery);

  // Label each segment ("Channel: …", "Source: …") so the data CONTEXT reads
  // unambiguously — e.g. "Channel: YouTube" (the query reads the YouTube table) is clearly distinct
  // from a filter like "Channel is YouTube" (the cross-channel table narrowed to a channel value).
  const channels = uniq(scpQueries.map(q => getChannelPrettyName(q.channel.id)));
  if (channels.length > 0) {
    segments.push(`Channel: ${channels.join(', ')}`);
  }

  // Raw KPI counts include activity no platform claims, so they never match attributed KPI results.
  if (rawKpiQueries.length > 0) {
    segments.push(RAW_KPI_COUNTS_LABEL);
  }

  // `sources` comes from saved JSON, so guard it rather than assume the array.
  const sources = uniq([
    ...scpQueries.map(q => sourceDisplay(q.source)),
    ...rawKpiQueries.flatMap(q => (Array.isArray(q.sources) ? q.sources : []))
  ]);
  if (sources.length > 0) {
    segments.push(`Source: ${sources.join(', ')}`);
  }

  if (includeKpi) {
    const kpis = uniq(queries.filter(isKpiQuery).map(q => q.kpi.id));
    if (kpis.length > 0) {
      segments.push(`KPI: ${kpis.join(', ')}`);
    }
  }

  return segments;
};

/**
 * The filters a chart's queries apply, one readable segment each ("Platform is Amazon", "Spend >
 * $1,000"), de-duplicated across a multi-query chart: dimension filters first, then metric filters.
 * These describe ONLY the explicit backend query filters (`QuerySpec.filters` and
 * `QuerySpec.metricFilters`) — never the conditions MetricQueryBuilder generates — and are unrelated
 * to client-side table narrowing.
 */
export const buildChartFilterSegments = (
  queries: QuerySpec[],
  { fieldMetaCtx, currencyByRef = {} }: ChartContextOptions = {}
): string[] => [
  ...uniq(
    queries.flatMap(q =>
      (Array.isArray(q.filters) ? q.filters : []).map(formatFilterNode).filter(Boolean)
    )
  ),
  ...uniq(
    queries.flatMap(q =>
      // hasMetricFilters covers the non-array/empty case; isValidMetricFilter covers a malformed
      // entry WITHIN an otherwise-valid array (see comment above its definition).
      hasMetricFilters(q)
        ? (q.metricFilters ?? [])
            .filter(isValidMetricFilter)
            .map(f =>
              formatMetricFilter(
                q.queryId,
                f,
                q.dimensions,
                fieldMetaCtx,
                currencyByRef[`${q.queryId}.${f.metric.id}`]
              )
            )
        : []
    )
  )
];

/**
 * The filter segments for a chart as it is actually fetched. A chart with a breakdown control saves
 * every breakdown option in its dimensions, but each fetch is narrowed to the active one (the
 * viewer's pick, else the control's default — see withActiveBreakdown), and a metric filter with no
 * grain of its own is tested at those narrowed dimensions. So the grain prefix has to come from the
 * narrowed queries, or it names every option ("Platforms by campaign with…") whatever is picked.
 */
export const buildActiveChartFilterSegments = (
  config: ChartConfig,
  breakdown: DimensionId | null | undefined,
  options: ChartContextOptions = {}
): string[] => {
  const active = activeBreakdownFor(config, breakdown);
  const fetched = active ? withActiveBreakdown(config, active) : config;
  return buildChartFilterSegments(fetched.queries, options);
};
